import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Empty, Spin, Tag, Timeline, Typography} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DownOutlined,
    MinusCircleOutlined,
    OrderedListOutlined,
    SyncOutlined,
    UpOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import chatService from '../../services/chatService';
import {TaskPlan, TaskPlanItem} from '../../types/chat';
import MarkdownRenderer from '../common/MarkdownRenderer';

const {Text, Title} = Typography;

// 子任务分组：一段连续的消息，对应任务规划中某个 itemIndex 的执行过程
interface SubTaskGroup {
    planId: string | null;
    itemIndex: number;
    messages: any[];
}

interface Props {
    planId: string;
    pollInterval?: number;
    createdTime?: string;
    subTaskGroups?: SubTaskGroup[];
    onChildSessionClick?: (childSessionId: number) => void;
    active?: boolean;
}

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
    PENDING: {color: '#8c8c8c', icon: <ClockCircleOutlined/>, label: '等待中'},
    EXECUTING: {color: 'var(--pt-blue)', icon: <SyncOutlined spin/>, label: '执行中'},
    COMPLETED: {color: 'var(--pt-bull)', icon: <CheckCircleOutlined/>, label: '已完成'},
    FAILED: {color: 'var(--pt-bear)', icon: <CloseCircleOutlined/>, label: '失败'},
    SKIPPED: {color: 'var(--pt-amber)', icon: <MinusCircleOutlined/>, label: '已跳过'},
};

const planStatusConfig: Record<string, { color: string; label: string }> = {
    PLANNED: {color: '#8c8c8c', label: '已规划'},
    EXECUTING: {color: 'var(--pt-blue)', label: '执行中'},
    COMPLETED: {color: 'var(--pt-bull)', label: '已完成'},
    FAILED: {color: 'var(--pt-bear)', label: '失败'},
};

const renderItemDescription = (item: TaskPlanItem) => {
    const desc = item.itemDescription || '';
    if (desc.length <= 80) return desc;
    return desc.substring(0, 80) + '...';
};

// 分离思考过程与实际内容（与主对话流parseAIContent逻辑一致，避免重复实现）
const parseAIContent = (content: string) => {
    let decoded = content
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
    const thinkingRegex = /<thinking>([\s\S]*?)<\/thinking>/i;
    const thinkingMatch = decoded.match(thinkingRegex);
    if (thinkingMatch) {
        const thinkContent = thinkingMatch[1].trim();
        let actualContent = decoded.replace(thinkingMatch[0], '').trim()
            .replace(/<think>/gi, '').replace(/<\/think>/gi, '').trim()
            .replace(/===\s*(.+?)\s*===/g, '## $1');
        return {thinkContent: thinkContent || null, actualContent: actualContent || '（无内容）'};
    }
    if (decoded.includes('<think>')) {
        const formattedContent = decoded.replace(/<think>/gi, '').replace(/<\/think>/gi, '').trim()
            .replace(/===\s*(.+?)\s*===/g, '## $1');
        return {thinkContent: null, actualContent: formattedContent || '（无内容）'};
    }
    const formattedContent = content.replace(/===\s*(.+?)\s*===/g, '## $1');
    return {thinkContent: null, actualContent: formattedContent};
};

const toolNameMap: Record<string, string> = {
    get_kline: 'K线查询', get_position: '持仓查询', get_balance: '余额查询',
    trade: '交易执行', cancel_order: '撤销订单', set_stop_order: '设置止盈止损',
    get_candle_patterns: 'K线形态', get_zone_patterns: '区域形态',
    get_radar: '多空雷达', get_long_short_ratio: '多空比',
    submit_final_output: '最终决策',
};
const getToolDisplayName = (fn?: string) => fn ? (toolNameMap[fn] || fn) : '工具调用';

const SubTaskMessageItem: React.FC<{ msg: any }> = ({msg}) => {
    const isUser = 'user' === msg.role;
    const isTool = 'tool' === msg.role;
    const isFunctionCall = msg.content?.startsWith('[function_call]');
    const [toolExpanded, setToolExpanded] = useState(false);
    const [thinkExpanded, setThinkExpanded] = useState(false);

    if (isUser) {
        const preview = (msg.content || '').substring(0, 150) + ((msg.content || '').length > 150 ? '...' : '');
        return (
            <div style={{marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid #F0B429'}}>
                <Text style={{fontSize: 10, color: 'rgba(255,255,255,0.4)'}}>触发</Text>
                <div style={{fontSize: 12, color: 'rgba(255,255,255,0.6)', whiteSpace: 'pre-wrap'}}>{preview}</div>
            </div>
        );
    }

    if (isFunctionCall) {
        const callText = msg.content.replace('[function_call] ', '');
        const fnName = msg.functionName || 'unknown';
        return (
            <div style={{marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid var(--pt-amber)'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer'}}
                     onClick={() => setToolExpanded(!toolExpanded)}>
                    <Text style={{fontSize: 10, color: 'var(--pt-amber)'}}>🔧 {getToolDisplayName(fnName)}</Text>
                    <Text style={{fontSize: 10, color: 'rgba(255,255,255,0.3)'}}>
                        {toolExpanded ? '收起' : '展开'}
                    </Text>
                </div>
                {toolExpanded && (
                    <div style={{fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4, whiteSpace: 'pre-wrap'}}>
                        {callText}
                    </div>
                )}
            </div>
        );
    }

    if (isTool) {
        const fnName = msg.functionName || '';
        return (
            <div style={{marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid var(--pt-amber)'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer'}}
                     onClick={() => setToolExpanded(!toolExpanded)}>
                    <Text style={{fontSize: 10, color: 'var(--pt-amber)'}}>📊 {getToolDisplayName(fnName)}结果</Text>
                    <Text style={{fontSize: 10, color: 'rgba(255,255,255,0.3)'}}>
                        {toolExpanded ? '收起' : '展开'}
                    </Text>
                </div>
                {toolExpanded && (
                    <div style={{
                        marginTop: 4, padding: 8, background: '#0d0d14',
                        borderRadius: 4, border: '1px solid #1a1a28',
                        maxHeight: 200, overflowY: 'auto',
                    }}>
                        <MarkdownRenderer content={msg.content || ''} showCopyButton={false}/>
                    </div>
                )}
            </div>
        );
    }

    const {thinkContent, actualContent} = parseAIContent(msg.content || '');
    return (
        <div style={{marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid var(--pt-bull)'}}>
            <Text style={{fontSize: 10, color: 'var(--pt-bull)'}}>AI</Text>
            {thinkContent && (
                <div style={{marginTop: 2}}>
                    <button onClick={() => setThinkExpanded(!thinkExpanded)}
                            style={{
                                fontSize: 10, color: 'rgba(255,255,255,0.4)',
                                background: 'none', border: 'none', cursor: 'pointer'
                            }}>
                        {thinkExpanded ? '🙈 隐藏思考' : '🤔 查看思考'}
                    </button>
                    {thinkExpanded && (
                        <div style={{
                            fontSize: 11, color: 'rgba(255,255,255,0.4)',
                            whiteSpace: 'pre-wrap', marginTop: 4
                        }}>{thinkContent}</div>
                    )}
                </div>
            )}
            <div style={{
                marginTop: 4, padding: 6, background: '#0d0d14',
                borderRadius: 4, border: '1px solid #1a1a28',
                maxHeight: 250, overflowY: 'auto'
            }}>
                <MarkdownRenderer content={actualContent} showCopyButton={false}/>
            </div>
        </div>
    );
};

const SubTaskConversation: React.FC<{ group: SubTaskGroup }> = ({group}) => {
    return (
        <div style={{
            marginTop: 8, padding: 10, background: '#111118',
            border: '1px solid #2a2a3e', borderRadius: 6,
            maxHeight: 400, overflowY: 'auto',
        }}>
            {group.messages.map((msg, idx) => (
                <SubTaskMessageItem key={idx} msg={msg}/>
            ))}
        </div>
    );
};

const AgentTaskPlanInline: React.FC<Props> = ({
                                                  planId,
                                                  pollInterval = 3000,
                                                  createdTime,
                                                  subTaskGroups,
                                                  onChildSessionClick,
                                                  active = true
                                              }) => {
    const [plan, setPlan] = useState<TaskPlan | null>(null);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    // 每个 item 展开状态：itemIndex -> 是否展开
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchPlan = useCallback(async () => {
        if (!planId) return;
        try {
            const data = await chatService.getPlan(planId);
            setPlan(data);
        } catch (e) {
            console.error('获取任务计划失败:', planId, e);
        }
    }, [planId]);

    useEffect(() => {
        if (!planId || !active) return;
        setLoading(true);
        fetchPlan().finally(() => setLoading(false));

        timerRef.current = setInterval(fetchPlan, pollInterval);
        return () => {
            if (timerRef.current !== null) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [planId, pollInterval, fetchPlan, active]);

    const completedCount = plan?.items?.filter(i => i.status === 'COMPLETED').length ?? 0;
    const totalCount = plan?.items?.length ?? 0;
    const isFinished = plan?.status === 'COMPLETED' || plan?.status === 'FAILED';

    // 计划完成后停止轮询
    useEffect(() => {
        if (isFinished && timerRef.current !== null) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, [isFinished]);

    const toggleItem = (itemIndex: number) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(itemIndex)) {
                next.delete(itemIndex);
            } else {
                next.add(itemIndex);
            }
            return next;
        });
    };

    // 按 itemIndex 查找子任务分组
    const findSubTaskGroup = (itemIndex: number): SubTaskGroup | null => {
        if (!subTaskGroups) return null;
        return subTaskGroups.find(g => g.itemIndex === itemIndex) || null;
    };

    if (loading && !plan) {
        return (
            <div style={{
                marginTop: 8,
                padding: '16px',
                background: '#1a1a2e',
                border: '1px solid #2a2a4e',
                borderRadius: 8,
                textAlign: 'center',
            }}>
                <Spin size="small"/>
                <Text style={{color: 'rgba(255,255,255,0.45)', marginLeft: 8, fontSize: 13}}>
                    加载任务规划...
                </Text>
            </div>
        );
    }

    if (!plan) {
        return (
            <div style={{
                marginTop: 8,
                padding: '12px 14px',
                background: '#1a1a2e',
                border: '1px solid #2a2a4e',
                borderRadius: 8,
            }}>
                <Empty description="未找到任务规划" image={Empty.PRESENTED_IMAGE_SIMPLE}/>
            </div>
        );
    }

    return (
        <div style={{
            marginTop: 8,
            marginBottom: 16,
            padding: '12px 14px',
            background: '#1a1a2e',
            border: '1px solid #2a2a4e',
            borderRadius: 8,
        }}>
            {/* 头部：标题 + 状态 + 折叠按钮 */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
            }} onClick={() => setCollapsed(!collapsed)}>
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <OrderedListOutlined style={{color: 'var(--pt-blue)', fontSize: 15}}/>
                    <Text strong style={{color: '#e8e8e8', fontSize: 14}}>
                        {plan.title}
                    </Text>
                    <Tag color={planStatusConfig[plan.status]?.color} style={{margin: 0}}>
                        {planStatusConfig[plan.status]?.label}
                    </Tag>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <Text style={{color: 'rgba(255,255,255,0.45)', fontSize: 12}}>
                        {completedCount}/{totalCount}
                    </Text>
                    {collapsed ? (
                        <UpOutlined style={{color: 'rgba(255,255,255,0.45)', fontSize: 12}}/>
                    ) : (
                        <DownOutlined style={{color: 'rgba(255,255,255,0.45)', fontSize: 12}}/>
                    )}
                </div>
            </div>

            {/* 进度条 */}
            {totalCount > 0 && (
                <div style={{
                    height: 4,
                    background: '#333',
                    borderRadius: 2,
                    overflow: 'hidden',
                    marginTop: 10,
                }}>
                    <div style={{
                        height: '100%',
                        width: `${(completedCount / totalCount) * 100}%`,
                        background: 'linear-gradient(90deg, var(--pt-blue), var(--pt-bull))',
                        borderRadius: 2,
                        transition: 'width 0.5s ease',
                    }}/>
                </div>
            )}

            {/* 可折叠内容 */}
            {!collapsed && (
                <div style={{marginTop: 12}}>
                    {/* 完成后显示摘要 */}
                    {isFinished && plan.summaryText && (
                        <div style={{
                            background: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: 6,
                            padding: 10,
                            marginBottom: 12,
                        }}>
                            <Text style={{fontSize: 13, color: '#b0b0b0'}}>
                                {plan.summaryText}
                            </Text>
                        </div>
                    )}

                    {/* Timeline 任务列表 */}
                    <Timeline
                        style={{padding: '4px 0'}}
                        items={plan.items?.map((item) => {
                            const config = statusConfig[item.status] || statusConfig.PENDING;
                            const subTaskGroup = findSubTaskGroup(item.itemIndex);
                            const hasConversation = null != subTaskGroup && subTaskGroup.messages.length > 0;
                            const isItemExpanded = expandedItems.has(item.itemIndex);
                            return {
                                color: config.color,
                                dot: config.icon,
                                children: (
                                    <div style={{
                                        background: '#1a1a1a',
                                        border: '1px solid #2a2a2a',
                                        borderRadius: 6,
                                        padding: '8px 10px',
                                        marginBottom: 4,
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 4,
                                        }}>
                                            <Text strong style={{
                                                color: 'COMPLETED' === item.status
                                                    ? 'var(--pt-bull)' : 'FAILED' === item.status
                                                        ? 'var(--pt-bear)' : '#e8e8e8',
                                                fontSize: 13,
                                            }}>
                                                {item.itemIndex + 1}. {item.itemTitle}
                                            </Text>
                                            <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
                                                <Tag
                                                    color={'CONCURRENT' === item.executionMode ? 'purple' : 'blue'}
                                                    style={{fontSize: 10, lineHeight: '16px', margin: 0}}
                                                >
                                                    {'CONCURRENT' === item.executionMode ? '并发' : '顺序'}
                                                </Tag>
                                                <Tag
                                                    color={config.color}
                                                    style={{fontSize: 10, lineHeight: '16px', margin: 0}}
                                                >
                                                    {config.label}
                                                </Tag>
                                            </div>
                                        </div>
                                        <Text
                                            type="secondary"
                                            style={{fontSize: 11, display: 'block', marginTop: 4}}
                                        >
                                            {renderItemDescription(item)}
                                        </Text>
                                        {item.totalTimeMs > 0 && (
                                            <Text type="secondary" style={{fontSize: 11}}>
                                                耗时: {(item.totalTimeMs / 1000).toFixed(1)}s
                                                {item.totalTurns > 0 && ` | ${item.totalTurns}轮`}
                                            </Text>
                                        )}
                                        {item.errorMessage && (
                                            <Text type="danger" style={{fontSize: 11, display: 'block', marginTop: 2}}>
                                                错误: {item.errorMessage}
                                            </Text>
                                        )}
                                        {/* 子智能体独立子会话结果摘要（.bug 第①项：子会话结论回传父会话任务规划卡） */}
                                        {item.agentAlias && item.childSummary && (
                                            <div style={{
                                                marginTop: 6,
                                                padding: '6px 10px',
                                                background: '#0d0d14',
                                                borderRadius: 4,
                                                border: '1px solid rgba(82, 196, 26, 0.25)',
                                            }}>
                                                <Text style={{
                                                    fontSize: 11,
                                                    color: 'var(--pt-bull)',
                                                    display: 'block',
                                                    marginBottom: 2
                                                }}>
                                                    🤖 子智能体结论
                                                </Text>
                                                <Text style={{
                                                    fontSize: 12,
                                                    color: 'rgba(255,255,255,0.75)',
                                                    whiteSpace: 'pre-wrap',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 4,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}>
                                                    {item.childSummary.length > 400 ? item.childSummary.substring(0, 400) + '...' : item.childSummary}
                                                </Text>
                                            </div>
                                        )}
                                        {/* 子智能体会话导航按钮 */}
                                        {item.agentAlias && item.childSessionId && onChildSessionClick && (
                                            <div style={{marginTop: 6}}>
                                                <button
                                                    onClick={() => onChildSessionClick(Number(item.childSessionId))}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: 11,
                                                        backgroundColor: 'rgba(82, 196, 26, 0.1)',
                                                        border: '1px solid rgba(82, 196, 26, 0.3)',
                                                        borderRadius: 4,
                                                        color: 'var(--pt-bull)',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    🤖 {item.status === 'COMPLETED' ? '查看子智能体对话' : '查看子智能体会话'}
                                                </button>
                                            </div>
                                        )}
                                        {/* 子任务对话展开按钮 */}
                                        {hasConversation && (
                                            <div style={{marginTop: 6}}>
                                                <button
                                                    onClick={() => toggleItem(item.itemIndex)}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: 11,
                                                        backgroundColor: 'rgba(24, 144, 255, 0.1)',
                                                        border: '1px solid rgba(24, 144, 255, 0.3)',
                                                        borderRadius: 4,
                                                        color: 'var(--pt-blue)',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {isItemExpanded ? '▲ 收起对话' : '▼ 查看对话'}
                                                    {' '}({subTaskGroup!.messages.length}条)
                                                </button>
                                                {isItemExpanded && (
                                                    <SubTaskConversation group={subTaskGroup!}/>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ),
                            };
                        })}
                    />
                </div>
            )}
            {createdTime && (
                <div style={{marginTop: 8}}>
                    <Text style={{fontSize: 11, color: 'rgba(255, 255, 255, 0.45)'}}>
                        {dayjs(createdTime).format('YYYY-MM-DD HH:mm:ss')}
                    </Text>
                </div>
            )}
        </div>
    );
};

export default AgentTaskPlanInline;